/**
 * Main application component that sets up routing and application structure
 */
import React, { JSX, useEffect } from 'react';
import { useAuth } from '@/app/providers/AuthContext';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Pages
import { LandingPage } from "@/features/landing";
import { DashboardPage } from "@/features/dashboard";
import SettingsPage from '@/pages/SettingsPage';
import NotFoundPage from '@/pages/NotFoundPage';
import GithubAppCallback from '@/features/auth/components/GithubAppCallback';
import { RepositoryDetailsPage } from "@/features/repository";
import { RulesLibraryPage } from "@/features/rules";
import { BlogPage } from "@/features/blog";
import { BlogPostPage } from "@/features/blog";
import { DocsContentPage } from "@/docs";
import { PullRequestPage } from "@/features/pull-request";

// Components
import { ProtectedRoute } from "@/features/auth";
import { AuthCallback } from "@/features/auth";

// Context and hooks
import { AuthProvider } from '@/app/providers/AuthContext';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

// Main application component
const AppContent: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Landing page doesn't use the app layout
  if (location.pathname === '/' && !isAuthenticated) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Routes>
        {/* Public routes */}
        <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" /> : <LandingPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/docs" element={<Navigate to="/docs/Welcome" />} />
        <Route path="/docs/:topic" element={<DocsContentPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />

        {/* Protected routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } />
        <Route path="/repositories" element={
          <ProtectedRoute>
            <Navigate to="/dashboard?tab=repositories" replace />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } />
        <Route path="/repository/:repoId" element={
          <ProtectedRoute>
            <RepositoryDetailsPage />
          </ProtectedRoute>
        } />
        <Route path="/repository/:repoId/pr/:prNumber" element={
          <ProtectedRoute>
            <PullRequestPage />
          </ProtectedRoute>
        } />
        <Route path="/rules" element={
          <ProtectedRoute>
            <RulesLibraryPage />
          </ProtectedRoute>
        } />
        <Route path="/github-callback" element={<GithubAppCallback />} />

        {/* 404 route */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </div>
  );
};

// Wrap the app with necessary providers
export default function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>

        <AuthProvider>
          <AppContent />
        </AuthProvider>

    </QueryClientProvider>
  );
}
